/**
 * Last-resort recovery for images that fail because the session lapsed.
 *
 * Gallery photos are plain <img> tags, so the browser — not api.js — issues
 * those requests. There is no promise to catch, which means the 401-refresh-
 * replay path in api.js cannot help them: a private gallery's photos simply
 * render broken until the page is reloaded by hand.
 *
 * authService renews ahead of expiry, so in normal use nothing here ever fires.
 * This exists for the cases that slip through — a renewal lost to a network
 * blip, a machine resuming with a stale clock — because the symptom (a gallery
 * of broken images) is bad enough to be worth a safety net.
 *
 * One delegated listener rather than per-component error handling: image error
 * events do not bubble, so a capture-phase listener on the document catches
 * every image in the app from a single place.
 */
import api from "@api/api.js";
import config from "@/config.js";

const RETRIED_FLAG = "sessionRetried";

let pendingRecovery = null;

function isProtectedImage(element) {
  if (!(element instanceof HTMLImageElement)) return false;
  const src = element.getAttribute("src") || "";
  return src.startsWith(config.imageBase);
}

/** Re-request an image by reassigning the src the browser already gave up on. */
function reloadImage(image) {
  const src = image.getAttribute("src");
  image.removeAttribute("src");
  image.setAttribute("src", src);
}

async function recover(image) {
  // Retry each image once. A genuinely missing or forbidden file must be
  // allowed to stay broken rather than loop.
  if (image.dataset[RETRIED_FLAG]) return;
  image.dataset[RETRIED_FLAG] = "1";

  // A failing gallery produces dozens of error events at once; they share one
  // renewal so the refresh token is not rotated dozens of times, which would
  // otherwise look exactly like token theft and revoke the whole session.
  pendingRecovery = pendingRecovery || api.renewSession();
  const renewed = await pendingRecovery;
  pendingRecovery = null;

  if (renewed) reloadImage(image);
}

export function initImageSessionRecovery() {
  document.addEventListener(
    "error",
    (event) => {
      if (isProtectedImage(event.target)) recover(event.target);
    },
    true // capture: error events from <img> do not bubble
  );
}

export default initImageSessionRecovery;
